/**
 * A player's MAIN position group in one league + season — Catalyst's position-template rule
 * (track2-plan §8.6): the position she played the most minutes at → the group that holds it.
 *
 * One rule in one place, because three things must agree on it (T2-5a): the Bio tag, the Stats
 * tile's choice of comparison group, and the nightly job that puts each player in exactly one
 * group. If they disagreed, a tile would compare her with a group she isn't counted in.
 *
 * Input = minutes per position (`/player-stats` with `groupBy: ["position_id"]` — or, in the
 * job, one player's rows of a `["player_id", "position_id"]` query).
 */

import { PEER_POSITION_GROUPS } from "./position-groups.js";

const GROUP_OF_POSITION = new Map<string, string>(
  PEER_POSITION_GROUPS.flatMap((g) => g.positionIds.map((p) => [p, g.id] as const)),
);

/** The group a position code belongs to (e.g. "LCM" → "midfielder"). A code no group holds
 *  would be a new code in the DB — thrown, not guessed. */
export function positionGroupOf(position: string): string {
  const group = GROUP_OF_POSITION.get(position);
  if (!group) throw new Error(`Position "${position}" isn't in any position group`);
  return group;
}

/**
 * The group of the position with the most minutes; null when there are no rows (no minutes
 * there — an unused sub). A tie goes to the position code first in alphabetical order: SQL rows
 * come in no fixed order, so "the first row" could differ between the site and the nightly job.
 * A position no group holds would be a new code in the DB — thrown, not guessed.
 */
export function mainPositionGroup(rows: ReadonlyArray<Record<string, unknown>>): string | null {
  let top: { position: string; minutes: number } | null = null;
  for (const row of rows) {
    const position = row.position_id;
    const minutes = row.minutes;
    if (typeof position !== "string" || typeof minutes !== "number") {
      throw new Error(`Unexpected minutes-by-position row: ${JSON.stringify(row)}`);
    }
    if (top === null || minutes > top.minutes || (minutes === top.minutes && position < top.position)) {
      top = { position, minutes };
    }
  }
  if (top === null) return null;
  return positionGroupOf(top.position);
}
